import { useState, useEffect, useCallback } from "react";
import { Loader2, AlertCircle, RefreshCw, Users } from "lucide-react";
import { ApiService } from "../../../../services/api";
import { StudentReportType } from "../../../../types/student-report";
import StudentReportDisplay from "./StudentReportDisplay";

interface ChildInfo {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    phone?: string;
    department?: {
        id: string;
        name: string;
        color?: string;
    };
    metadata?: Record<string, any>;
    media?: {
        id: string;
        url: string;
    };
}

interface ChildReport {
    student: ChildInfo;
    summary: any;
    lessonStatistics: any[];
    topicStatistics: any[];
    reportType: StudentReportType;
    error?: string;
}

interface ParentReportsData {
    totalChildren: number;
    children: ChildReport[];
    message?: string;
}

export default function ParentReportsTab() {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [data, setData] = useState<ParentReportsData | null>(null);
    const [selectedChildId, setSelectedChildId] = useState<string | null>(null);

    const loadData = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const result = await ApiService.getParentChildrenReports({
                type: "all",
            });
            setData(result);

            if (result.children && result.children.length > 0 && !selectedChildId) {
                setSelectedChildId(result.children[0].student.id);
            }
        } catch (err: any) {
            console.error("Error loading parent children reports:", err);
            setError(err.message || "Veri yüklenirken bir hata oluştu.");
        } finally {
            setLoading(false);
        }
    }, [selectedChildId]);

    useEffect(() => {
        loadData();
    }, []);

    if (loading && !data) {
        return (
            <div className="flex flex-col items-center justify-center py-12">
                <Loader2 className="w-10 h-10 text-blue-600 animate-spin mb-4" />
                <p className="text-gray-600">Çocuk bilgileri yükleniyor...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex flex-col items-center justify-center py-12 text-center">
                <AlertCircle className="w-16 h-16 text-red-300 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">Hata Oluştu</h3>
                <p className="text-gray-500 max-w-md mb-4">{error}</p>
                <button
                    onClick={loadData}
                    className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Tekrar Dene
                </button>
            </div>
        );
    }

    if (!data || data.totalChildren === 0) {
        return (
            <div className="flex flex-col items-center justify-center py-12 text-center">
                <Users className="w-16 h-16 text-gray-300 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">Kayıtlı Çocuk Bulunamadı</h3>
                <p className="text-gray-500 max-w-md">
                    {data?.message || "Hesabınıza kayıtlı öğrenci bulunmamaktadır. Lütfen sistem yöneticinize başvurun."}
                </p>
            </div>
        );
    }

    return (
        <div className="h-full flex flex-col">

            <div className="flex-shrink-0 mb-4">
                <div className="flex items-center gap-3 overflow-x-auto pb-2 scrollbar-hide">
                    {data.children.map((child) => {
                        const isSelected = selectedChildId === child.student.id;
                        const initials = `${child.student.firstName?.[0] || ""}${child.student.lastName?.[0] || ""}`.toUpperCase();

                        return (
                            <button
                                key={child.student.id}
                                onClick={() => setSelectedChildId(child.student.id)}
                                className={`flex items-center gap-3 px-4 py-3 rounded-xl border-2 transition-all duration-200 flex-shrink-0 ${isSelected
                                    ? "bg-blue-50 border-blue-500 shadow-md"
                                    : "bg-white border-gray-200 hover:border-blue-300 hover:bg-gray-50"
                                    }`}>

                                <div
                                    className={`w-10 h-10 rounded-lg flex-shrink-0 flex items-center justify-center overflow-hidden ${isSelected ? "ring-2 ring-blue-400 ring-offset-1" : ""
                                        }`}
                                    style={{
                                        backgroundColor: child.student.media?.url ? "transparent" : (child.student.department?.color || "#6366f1"),
                                    }}>
                                    {child.student.media?.url ? (
                                        <img
                                            src={child.student.media.url}
                                            alt={`${child.student.firstName} ${child.student.lastName}`}
                                            className="w-full h-full object-cover"
                                        />
                                    ) : (
                                        <span className="text-white font-bold text-sm">{initials}</span>
                                    )}
                                </div>

                                <div className="flex flex-col items-start min-w-0">
                                    <span
                                        className={`font-semibold text-sm truncate max-w-[120px] ${isSelected ? "text-blue-700" : "text-gray-900"
                                            }`}>
                                        {child.student.firstName} {child.student.lastName}
                                    </span>
                                    {child.student.department && (
                                        <span
                                            className="text-xs truncate max-w-[120px]"
                                            style={{ color: child.student.department.color || "#6b7280" }}>
                                            {child.student.department.name}
                                        </span>
                                    )}
                                </div>

                                {child.summary && (
                                    <div
                                        className={`flex-shrink-0 px-2 py-1 rounded-md text-xs font-bold ${child.summary.overallSuccessPercent >= 70
                                            ? "bg-green-100 text-green-700"
                                            : child.summary.overallSuccessPercent >= 50
                                                ? "bg-yellow-100 text-yellow-700"
                                                : "bg-red-100 text-red-700"
                                            }`}>
                                        %{child.summary.overallSuccessPercent}
                                    </div>
                                )}
                            </button>
                        );
                    })}
                </div>
            </div>

            <div className="flex-1 min-h-0 overflow-y-auto">
                {selectedChildId ? (
                    <StudentReportDisplay
                        userId={selectedChildId}
                        reportType="all"
                        selectedMainLessonFilter={null}
                        selectedTopicFilter={null}
                    />
                ) : (
                    <div className="flex flex-col items-center justify-center py-12 text-center">
                        <Users className="w-16 h-16 text-gray-300 mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 mb-2">Çocuk Seçin</h3>
                        <p className="text-gray-500 max-w-md">Raporlarını görüntülemek için yukarıdan bir çocuk seçin.</p>
                    </div>
                )}
            </div>
        </div>
    );
}